import { create } from 'zustand';

export type UploadStatus = 'pending' | 'uploading' | 'processing' | 'completed' | 'failed';

export interface PendingUpload {
  id: string;
  status: UploadStatus;
  progress?: number;
  error?: string;
  receiptId?: string;
  pairWithListId?: string;
}

interface UploadState {
  uploads: Map<string, PendingUpload>;
  lastCompletedUploadId: string | null;

  startUpload: (id: string, pairWithListId?: string) => void;
  updateProgress: (id: string, progress: number) => void;
  markProcessing: (id: string, receiptId: string) => void;
  completeUpload: (id: string, receiptId: string) => void;
  failUpload: (id: string, error: string) => void;
  removeUpload: (id: string) => void;
  clearCompleted: () => void;
  hasPendingUploads: () => boolean;
  getUploadForList: (listId: string) => PendingUpload | undefined;
}

let uploadCounter = 0;

export const generateUploadId = (): string => {
  return `upload-${++uploadCounter}-${Date.now()}`;
};

export const useUploadStore = create<UploadState>((set, get) => ({
  uploads: new Map(),
  lastCompletedUploadId: null,

  startUpload: (id, pairWithListId) => {
    set((state) => {
      const newUploads = new Map(state.uploads);
      newUploads.set(id, { id, status: 'uploading', pairWithListId });
      return { uploads: newUploads };
    });
  },

  updateProgress: (id, progress) => {
    set((state) => {
      const upload = state.uploads.get(id);
      if (!upload) return state;

      const newUploads = new Map(state.uploads);
      newUploads.set(id, { ...upload, progress });
      return { uploads: newUploads };
    });
  },

  markProcessing: (id, receiptId) => {
    set((state) => {
      const upload = state.uploads.get(id);
      if (!upload) return state;

      const newUploads = new Map(state.uploads);
      newUploads.set(id, { ...upload, status: 'processing', receiptId });
      return { uploads: newUploads };
    });
  },

  completeUpload: (id, receiptId) => {
    set((state) => {
      const upload = state.uploads.get(id);
      if (!upload) return state;

      const newUploads = new Map(state.uploads);
      newUploads.set(id, { ...upload, status: 'completed', receiptId });
      return { uploads: newUploads, lastCompletedUploadId: id };
    });
  },

  failUpload: (id, error) => {
    set((state) => {
      const upload = state.uploads.get(id);
      if (!upload) return state;

      const newUploads = new Map(state.uploads);
      newUploads.set(id, { ...upload, status: 'failed', error });
      return { uploads: newUploads };
    });
  },

  removeUpload: (id) => {
    set((state) => {
      const newUploads = new Map(state.uploads);
      newUploads.delete(id);
      return { uploads: newUploads };
    });
  },

  clearCompleted: () => {
    set((state) => {
      const newUploads = new Map(state.uploads);
      for (const [id, upload] of newUploads) {
        if (upload.status === 'completed') {
          newUploads.delete(id);
        }
      }
      return { uploads: newUploads };
    });
  },

  hasPendingUploads: () => {
    const state = get();
    for (const upload of state.uploads.values()) {
      if (upload.status === 'pending' || upload.status === 'uploading' || upload.status === 'processing') {
        return true;
      }
    }
    return false;
  },

  getUploadForList: (listId) => {
    const state = get();
    for (const upload of state.uploads.values()) {
      if (upload.pairWithListId === listId && (upload.status === 'pending' || upload.status === 'uploading' || upload.status === 'processing')) {
        return upload;
      }
    }
    return undefined;
  },
}));
